import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Link,
  Tabs,
  Tab,
} from "@mui/material";
import { MdExpandMore, MdDownload, MdLink } from "react-icons/md";

import { selectUserState, updateUser } from "@/store/slices/user.slice";
import { useAppSelector, useAppDispatch } from "@/hooks/common";
import { PageTitle } from "@/components/page.title";
import { HelpData, HelpItem } from "@/models/help";
import { loadHelpData } from "@/utils/load-help-data";
import {
  logHelpPageFirstOpened,
  logHelpPageEnvelopeTabClick,
  logHelpPageItemInteraction,
  logHelpPageHintInteraction,
  logHelpPageDownload,
  logHelpPageExternalLinkClick,
} from "@/firebase/logs";
import { incrementMaterialDownloadsCount, addDownloadedMaterial, getUserDoc } from "@/firebase/users";
import { DownloadConfirmationModal } from "@/components/modals/download.confirmation.modal";
import { useGetAllStages } from "@/firebase/stages/use.get.all.stages";
import { SelectedDifficulty } from "@/models/shared";
import { getBatchConfig } from "@/utils/get-batch-config";

import {
  ContentWrapper,
  HelpItemWrapper,
  SectionDivider,
  SectionHeader,
  ThumbnailContainer
} from "./styles";

export const Help = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUserState).user!;
  const selectedDifficulty = user.selected_difficulty as SelectedDifficulty | undefined;
  const { stages } = useGetAllStages();
  
  // Get batch configuration to check if multiple envelopes exist
  const batchConfig = getBatchConfig(user?.selected_batch);
  const hasMultipleEnvelopes = batchConfig?.hasMultipleEnvelopes ?? true; // Default to true for backward compatibility
  
  // All hooks must be called before any conditional returns
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [expandedHints, setExpandedHints] = useState<Set<string>>(new Set());
  const [selectedEnvelope, setSelectedEnvelope] = useState<number>(1);

  // Track logged interactions to avoid duplicate logging
  const [loggedInteractions, setLoggedInteractions] = useState<Set<string>>(new Set());

  // Download confirmation modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<{
    itemId: string;
    materialName: string;
    downloadUrl: string;
  } | null>(null);

  // Help data loading state
  const [helpData, setHelpData] = useState<HelpData | null>(null);
  const [helpDataLoading, setHelpDataLoading] = useState(true);

  // Load help data based on user's batch
  useEffect(() => {
    const loadData = async () => {
      try {
        setHelpDataLoading(true);
        const data = await loadHelpData(user?.selected_batch);
        setHelpData(data);
      } catch (error) {
        console.error("Failed to load help data:", error);
      } finally {
        setHelpDataLoading(false);
      }
    };
    loadData();
  }, [user?.selected_batch]);

  // --- Envelope completion logic (similar to select stage page) ---
  const solvedStageNumbers: number[] = useMemo(
    () => (Array.isArray(user?.solved_stages) ? user.solved_stages : []),
    [user?.solved_stages]
  );

  const isStageSolved = React.useCallback(
    (stage: any) => typeof stage?.stage_number === "number" && solvedStageNumbers.includes(stage.stage_number),
    [solvedStageNumbers]
  );

  // Filter stages by difficulty and envelope
  const difficultyFilteredStages = React.useMemo(() => {
    if (!stages) return [];
    if (!selectedDifficulty) return stages; // fallback: show all if no selection yet
    return stages.filter((s: any) => {
      const arr = s?.eligible_difficulty;
      if (!Array.isArray(arr) || arr.length === 0) return true; // backward compatible
      return arr.includes(selectedDifficulty);
    });
  }, [stages, selectedDifficulty]);

  const envelope1Stages = React.useMemo(
      () => difficultyFilteredStages.filter((s: any) => (s?.envelope ?? 1) === 1),
      [difficultyFilteredStages]
  );

  const isEnvelope1Complete = React.useMemo(() => {
    if (envelope1Stages.length === 0) return false; // only "complete" if there actually are envelope 1 stages
    return envelope1Stages.every(isStageSolved);
  }, [envelope1Stages, isStageSolved]);

  const lockEnvelope2 = !isEnvelope1Complete;

  // Log first opened
  useEffect(() => {
    const logKey = "first_opened";
    if (!loggedInteractions.has(logKey) && selectedDifficulty && user?.phone_number) {
      logHelpPageFirstOpened(user.phone_number, selectedDifficulty);
      setLoggedInteractions(prev => new Set(prev).add(logKey));
    }
  }, [user?.phone_number, selectedDifficulty, loggedInteractions]);

  // Extract help data (or use empty arrays/objects if loading)
  const helpDataTyped = helpData as HelpData | null;
  const helpItems = useMemo(() => helpDataTyped?.items || [], [helpDataTyped]);
  const materialEmojis = useMemo(() => helpDataTyped?.materialEmojis || {}, [helpDataTyped]);

  // Download limit constants
  const MAX_MATERIAL_DOWNLOADS = 3;
  const downloadedMaterials = new Set(user?.downloaded_materials || []);
  const currentDownloads = user?.material_downloads_count || 0;

  // Filter items by selected envelope and difficulty (for questions only)
  const filteredItems = useMemo(() => {
    const filtered = helpItems.filter(item => {
      // First, filter by envelope (skip if single envelope batch)
      if (hasMultipleEnvelopes) {
        let envelopeMatch = false;
        if (typeof item.envelope === 'number') {
          envelopeMatch = item.envelope === selectedEnvelope;
        } else {
          // For "1 & 2" items, show them in both envelopes
          envelopeMatch = true;
        }

        if (!envelopeMatch) return false;
      }

      // For questions, also filter by difficulty
      if (item.type === 'question') {
        // If no difficulty is selected, show all questions (fallback for backward compatibility)
        if (!selectedDifficulty) return true;

        // Check if the question is eligible for the selected difficulty
        const eligibleDifficulties = item.eligible_difficulty;

        if (!Array.isArray(eligibleDifficulties) || eligibleDifficulties.length === 0) {
          return true; // Show questions without difficulty restrictions
        }

        return eligibleDifficulties.includes(selectedDifficulty);
      }

      // For materials and digital items, always show them (no difficulty filtering)
      return true;
    });

    return filtered;
  }, [helpItems, selectedEnvelope, selectedDifficulty, hasMultipleEnvelopes]);

  // Group items by type and sort them
  const groupedItems = useMemo(() => {
    const groups = {
      question: [] as HelpItem[],
      material: [] as HelpItem[],
      digital: [] as HelpItem[],
    };

    filteredItems.forEach((item) => {
      groups[item.type].push(item);
    });

    return groups;
  }, [filteredItems]);

  // --- Wait until user data (difficulty) is loaded ---
  if (!user || !user.selected_difficulty) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Lade Hilfe-Informationen ...
        </Typography>
      </Box>
    );
  }

  const handleToggleExpanded = (itemId: string, item: HelpItem) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);

      // Log item interaction (first time only)
      const logKey = `item_${itemId}`;
      if (!loggedInteractions.has(logKey)) {
        logHelpPageItemInteraction(
          user.phone_number,
          itemId,
          item.type,
          item.title,
          item.envelope
        );
        setLoggedInteractions(prev => new Set(prev).add(logKey));
      }
    }
    setExpandedItems(newExpanded);
  };

  const handleToggleHintExpanded = (hintId: string, itemId: string, hintIndex: number) => {
    const newExpanded = new Set(expandedHints);
    if (newExpanded.has(hintId)) {
      newExpanded.delete(hintId);
    } else {
      newExpanded.add(hintId);

      // Log hint interaction (first time only)
      const logKey = `hint_${hintId}`;
      if (!loggedInteractions.has(logKey)) {
        logHelpPageHintInteraction(user.phone_number, itemId, hintIndex);
        setLoggedInteractions(prev => new Set(prev).add(logKey));
      }
    }
    setExpandedHints(newExpanded);
  };

  const handleEnvelopeTabChange = (newValue: number) => {
    const newEnvelope = newValue as 1 | 2;

    // Prevent switching to envelope 2 if it's locked
    if (newEnvelope === 2 && lockEnvelope2) {
      return;
    }

    setSelectedEnvelope(newEnvelope);

    // Log envelope tab click
    logHelpPageEnvelopeTabClick(user.phone_number, newEnvelope);
  };

  // Download confirmation handlers
  const handleMaterialDownloadClick = (itemId: string, materialName: string, downloadUrl: string) => {
    // Always show confirmation modal for material downloads
    setPendingDownload({ itemId, materialName, downloadUrl });
    setDownloadModalOpen(true);
  };

  const confirmMaterialDownload = async (itemId: string, materialName: string, downloadUrl: string) => {
    try {
      // Check if material has already been downloaded
      const isAlreadyDownloaded = downloadedMaterials.has(itemId);

      if (!isAlreadyDownloaded) {

        // Increment counter and add material to Firebase
        await incrementMaterialDownloadsCount(user.phone_number);
        await addDownloadedMaterial(user.phone_number, itemId);

        // Fetch updated user data from Firebase
        const updatedUser = await getUserDoc(user.phone_number);
        dispatch(updateUser(updatedUser));

      } else {
        console.log('Material already downloaded, skipping counter increment:', materialName);
      }

      // Log download
      logHelpPageDownload(user.phone_number, itemId, materialName, downloadUrl);

      // Create download link
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = materialName;
      link.click();
    } catch (error) {
      console.error('Error processing material download:', error);
    }
  };

  const handleDownloadConfirm = () => {
    if (pendingDownload) {
      confirmMaterialDownload(pendingDownload.itemId, pendingDownload.materialName, pendingDownload.downloadUrl);
    }
  };

  const handleDownloadModalClose = () => {
    setDownloadModalOpen(false);
    setPendingDownload(null);
  };

  const getHintsForDifficulty = (hints: HelpItem["hints"], difficulty: string) => {
    const difficultyHints = hints[difficulty as keyof HelpItem["hints"]];
    const generalHints = hints.general;

    // For each hint position, use difficulty-specific content if available, otherwise fall back to general
    return generalHints.map((generalHint, index) => {
      const difficultyHint = difficultyHints[index];
      return difficultyHint !== null ? difficultyHint : generalHint;
    }).filter(hint => hint && hint.trim() !== ''); // Remove empty hints
  };

  const getThumbnailIcon = (item: HelpItem) => {
    // Specific emojis for materials
    if (item.type === 'material') {
      return materialEmojis[item.title] || '📄';
    }

    // Fallback icons based on type
    switch (item.type) {
      case 'question':
        return '❓';
      case 'digital':
        return '💻';
      default:
        return '📋';
    }
  };

  // Check if any items in a section are expanded
  const hasExpandedItems = (items: HelpItem[]) => {
    return items.some(item => {
      const itemId = `${item.type}-${item.envelope}-${item.title}`;
      return expandedItems.has(itemId);
    });
  };

  const renderHelpItem = (item: HelpItem) => {
    const itemId = `${item.type}-${item.envelope}-${item.title}`;
    const isExpanded = expandedItems.has(itemId);
    const hints = getHintsForDifficulty(item.hints, selectedDifficulty ?? "easy");


    return (
      <HelpItemWrapper key={itemId}>
        <Accordion
          expanded={isExpanded}
          onChange={() => handleToggleExpanded(itemId, item)}
          sx={{
            backgroundColor: "transparent",
            boxShadow: "none",
            "&:before": {
              display: "none",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<MdExpandMore />}
            sx={{
              backgroundColor: "transparent",
              borderRadius: "12px",
              marginBottom: "12px",
              border: "none",
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: "16px",
                margin: "12px 0",
              },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
              <ThumbnailContainer>
                {getThumbnailIcon(item)}
              </ThumbnailContainer>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    marginBottom: "4px",
                    fontSize: "1.2rem",
                    color: "text.primary",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          {isExpanded && (
            <AccordionDetails sx={{ padding: 0, backgroundColor: "transparent", border: "none" }}>
              <Box sx={{ padding: "20px", backgroundColor: "rgba(0, 0, 0, 0.05)", borderRadius: "12px", border: "1px solid rgba(255, 255, 255, 0.05)" }}>

              {/* Thumbnails */}
              {item.assets && item.assets.length > 0 && item.assets.some((asset: any) => asset.thumbnail) && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      marginBottom: "16px",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    Vorschau:
                  </Typography>
                  <Box sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    marginBottom: "20px",
                    justifyContent: "center"
                  }}>
                    {item.assets.filter((asset: any) => asset.thumbnail).map((asset: any, index) => {
                      return (
                      <Box
                        key={index}
                        sx={{
                          position: "relative",
                          borderRadius: "8px",
                          overflow: "hidden",
                          border: "2px solid rgba(255, 255, 255, 0.1)",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor: "rgba(255, 255, 255, 0.3)",
                            transform: "scale(1.02)",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                          },
                        }}
                      >
                        <img
                          src={(asset as any).thumbnail}
                          alt={`Vorschau von ${asset.name}`}
                          style={{
                            width: "200px",
                            height: "auto",
                            maxHeight: "280px",
                            objectFit: "contain",
                            display: "block",
                          }}
                          onError={(e) => {
                            console.error('Failed to load thumbnail:', (asset as any).thumbnail, e);
                          }}
                          onLoad={() => {
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                            color: "white",
                            padding: "8px",
                            fontSize: "1rem",
                            textAlign: "center",
                            fontWeight: "medium",
                          }}
                        >
                          {asset.name.replace('.pdf', '')}
                        </Box>
                      </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              {/* Hints */}
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  marginBottom: "16px",
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontSize: "1.2rem",
                }}
              >
                Hinweise:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {hints.map((hint, index) => {
                  const hintId = `${itemId}-hint-${index}`;
                  const isHintExpanded = expandedHints.has(hintId);

                  return (
                    <Accordion
                      key={index}
                      expanded={isHintExpanded}
                      onChange={() => handleToggleHintExpanded(hintId, itemId, index)}
                      sx={{
                        backgroundColor: "transparent",
                        boxShadow: "none",
                        "&:before": {
                          display: "none",
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<MdExpandMore />}
                        sx={{
                          backgroundColor: "rgba(56, 56, 56, 0.6)",
                          borderRadius: "8px",
                          minHeight: "48px",
                          border: "1px solid rgba(255, 255, 255, 0.1)",
                          "& .MuiAccordionSummary-content": {
                            margin: "12px 0",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(56, 56, 56, 0.8)",
                            borderColor: "rgba(255, 255, 255, 0.2)",
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: "medium",
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: "0.3px",
                            fontSize: "1.2rem",
                          }}
                        >
                          Hinweis {index + 1}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: "12px 20px 20px", display: isHintExpanded ? "block" : "none" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: "pre-line",
                            lineHeight: 1.6,
                            color: "text.primary",
                            fontSize: "1.2rem",
                          }}
                        >
                          {hint}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>

              {/* Assets - only for non-material items */}
              {item.type !== 'material' && item.assets.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      marginTop: "16px",
                      marginBottom: "12px",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    Downloads:
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {item.assets.map((asset, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        startIcon={<MdDownload />}
                        component={Link}
                        href={asset.url}
                        target="_blank"
                        onClick={() => {
                          logHelpPageDownload(user.phone_number, itemId, asset.name, asset.url);
                        }}
                        sx={{
                          justifyContent: "flex-start",
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          color: "text.primary",
                          "&:hover": {
                            borderColor: "rgba(255, 255, 255, 0.4)",
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                          },
                        }}
                      >
                        {asset.name}
                      </Button>
                    ))}
                  </Box>
                </>
              )}

              {/* Material Download Button */}
              {item.type === 'material' && item.assets.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      marginTop: "16px",
                      marginBottom: "12px",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    Ersatz Material:
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<MdDownload />}
                    sx={{
                      justifyContent: "flex-start",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      color: "text.primary",
                      "&:hover": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                    onClick={() => {
                      const asset = item.assets[0]; // Use the first asset
                      handleMaterialDownloadClick(itemId, asset.name, asset.url);
                    }}
                  >
                    Ersatz Herunterladen
                  </Button>
                </>
              )}

              {/* Link */}
              {item.link && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: "bold",
                      marginTop: "16px",
                      marginBottom: "12px",
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontSize: "1.2rem",
                    }}
                  >
                    Externe Links:
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<MdLink />}
                    component={Link}
                    href={item.link}
                    target="_blank"
                    onClick={() => {
                      logHelpPageExternalLinkClick(user.phone_number, itemId, item.link!);
                    }}
                    sx={{
                      justifyContent: "flex-start",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                      color: "text.primary",
                      "&:hover": {
                        borderColor: "rgba(255, 255, 255, 0.4)",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                      },
                    }}
                  >
                    Link öffnen
                  </Button>
                </>
              )}
              </Box>
            </AccordionDetails>
          )}
        </Accordion>
      </HelpItemWrapper>
    );
  };

  // Show loading state
  if (helpDataLoading || !helpData) {
    return (
      <ContentWrapper>
        <PageTitle>HILFE & HINWEISE</PageTitle>
        <Box mt={4} textAlign="center">
          <Typography>Lade Hilfsinhalte...</Typography>
        </Box>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PageTitle>HILFE & HINWEISE</PageTitle>

      <Typography variant="body2" sx={{ marginBottom: "24px", color: "text.secondary", fontSize: "1.15rem" }}>
        Hier findest du alle wichtigen Informationen und Hinweise für das Spiel.
      </Typography>

      {/* Envelope Tabs - only show for batches with multiple envelopes */}
      {hasMultipleEnvelopes && (
        <Box sx={{ marginBottom: "24px" }}>
          <Tabs
            value={selectedEnvelope}
            onChange={(_, newValue) => handleEnvelopeTabChange(newValue)}
            centered
            sx={{
              "& .MuiTab-root": {
                fontSize: "1.3rem",
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "text.secondary",
              },
              "& .Mui-selected": {
                color: "primary.main",
              },
            }}
          >
            <Tab label="Umschlag 1" value={1} />
            <Tab
              label={lockEnvelope2 ? "🔒 Umschlag 2" : "Umschlag 2"}
              value={2}
              disabled={lockEnvelope2}
              sx={{
                opacity: lockEnvelope2 ? 0.5 : 1,
                cursor: lockEnvelope2 ? "not-allowed" : "pointer",
              }}
            />
          </Tabs>

          {/* Lock message for envelope 2 */}
          {lockEnvelope2 && (
            <Box sx={{
              textAlign: "center",
              mt: 2,
              p: 2,
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              border: "1px solid rgba(255, 255, 255, 0.1)"
            }}>
              <Typography variant="body2" color="text.secondary">
                🔒 Umschlag 2 ist gesperrt - Beantworte zuerst alle Fragen in Umschlag 1.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Questions Section */}
      {groupedItems.question.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "text.primary", textTransform: "uppercase", letterSpacing: "1px", fontSize: "1.4rem" }}>
              Fragen & Antworten
            </Typography>
          </SectionHeader>
          {groupedItems.question.map(renderHelpItem)}
          {hasExpandedItems(groupedItems.question) && <SectionDivider />}
        </>
      )}

      {/* Material Section */}
      {groupedItems.material.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "text.primary", textTransform: "uppercase", letterSpacing: "1px", fontSize: "1.4rem" }}>
              Materialien & Downloads
            </Typography>
          </SectionHeader>
          {groupedItems.material.map(renderHelpItem)}
          {hasExpandedItems(groupedItems.material) && <SectionDivider />}
        </>
      )}

      {/* Digital Section */}
      {groupedItems.digital.length > 0 && (
        <>
          <SectionHeader>
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "text.primary", textTransform: "uppercase", letterSpacing: "1px", fontSize: "1.4rem" }}>
              Digitale Tools & Links
            </Typography>
          </SectionHeader>
          {groupedItems.digital.map(renderHelpItem)}
        </>
      )}

      {/* Download Confirmation Modal */}
      <DownloadConfirmationModal
        open={downloadModalOpen}
        onClose={handleDownloadModalClose}
        onConfirm={handleDownloadConfirm}
        downloadsUsed={currentDownloads}
        maxDownloads={MAX_MATERIAL_DOWNLOADS}
        materialName={pendingDownload?.materialName || ""}
        isAlreadyDownloaded={pendingDownload ? downloadedMaterials.has(pendingDownload.itemId) : false}
      />
    </ContentWrapper>
  );
};
